import { useCallback, useEffect, useState } from 'react';
import { Title } from 'react-admin';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Stack,
    Typography
} from '@mui/material';
import { paymentsProxy } from './apiGateway';

function formatDeficitAmount(rawAmount: string): string | null {
    const compactValue = rawAmount.replace(/\s+/g, '').trim();
    if (!compactValue || !/^-?\d+(?:[.,]\d+)?$/.test(compactValue)) {
        return null;
    }

    const sign = compactValue.startsWith('-') ? '-' : '';
    const unsignedValue = sign ? compactValue.slice(1) : compactValue;
    const [integerPart, fractionPart] = unsignedValue.replace(',', '.').split('.');
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    if (fractionPart) {
        return `${sign}${formattedIntegerPart},${fractionPart}`;
    }

    return `${sign}${formattedIntegerPart}`;
}

export const SellerDeficitsPage = () => {
    const [loading, setLoading] = useState(true);
    const [deficitAmount, setDeficitAmount] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const loadDeficit = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await paymentsProxy.getSellersDeficit();
            const rawAmount = typeof response?.sellerDeficitAmount === 'string'
                ? response.sellerDeficitAmount
                : '';
            const formattedAmount = formatDeficitAmount(rawAmount);

            if (!formattedAmount) {
                throw new Error('Invalid seller deficit amount format');
            }

            setDeficitAmount(formattedAmount);
        } catch (loadError) {
            console.error('Failed to load sellers deficit:', loadError);
            setDeficitAmount(null);
            setError('Не удалось загрузить сумму дефицита');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadDeficit();
    }, [loadDeficit]);

    return (
        <Box sx={{ p: 2 }}>
            <Title title="Дефициты продавцов" />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                Дефициты
            </Typography>

            <Card sx={{ borderRadius: 2, maxWidth: 520 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1.5 }}>
                        Сумма дефицита продавцов
                    </Typography>

                    {loading ? (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <CircularProgress size={20} />
                            <Typography>Загрузка дефицита...</Typography>
                        </Stack>
                    ) : null}

                    {!loading && error ? (
                        <Stack spacing={1.5}>
                            <Alert severity="error">{error}</Alert>
                            <Box>
                                <Button variant="contained" onClick={() => void loadDeficit()}>
                                    Повторить
                                </Button>
                            </Box>
                        </Stack>
                    ) : null}

                    {!loading && !error ? (
                        <Stack direction="row" spacing={1} alignItems="baseline">
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                {deficitAmount || '-'}
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                ₽
                            </Typography>
                        </Stack>
                    ) : null}
                </CardContent>
            </Card>
        </Box>
    );
};
